
import type { PresenceEntry, PresenceUpdateInput } from "./presence.js";

export interface Ok<T = undefined> {
  ok: true;
  data?: T;
}
export interface Err {
  ok: false;
  error: { code: string; message?: string };
}
export type Ack<T = undefined> = (res: Ok<T> | Err) => void;

import type { AvProgress, PublicFile, PublicFolder, SharedFile } from "./files.js";
import type { PublicNotification } from "./notifications.js";
import type {
  PublicSupportMessage,
  AdminSupportMessage,
  PublicSupportTicket,
  TicketTypingPayload,
  TicketReadPayload,
} from "./support.js";

export interface UploadProgressInput {
  fileId: string;
  bytesUploaded: number;
  totalBytes: number;
}

export interface UserClientToServer {
  "user:ping": (ack: Ack<{ t: number; userId: string }>) => void;
  "user:upload.progress": (payload: UploadProgressInput, ack: Ack) => void;

  "user:support.join":  (payload: { ticketId: string }, ack: Ack) => void;
  "user:support.leave": (payload: { ticketId: string }, ack: Ack) => void;

  "user:support.typing": (payload: { ticketId: string; text: string }) => void;
}
export interface UserServerToClient {
  "user:notification": (n: PublicNotification) => void;
  "user:legal.updated": (payload: { slug: string; versionNumber: number; requireAcceptance: boolean }) => void;
  "user:session.invalidated": (reason: { code: string }) => void;
  "user:file.available":  (file: PublicFile) => void;
  "user:file.downloaded": (payload: { fileId: string; count: number }) => void;
  "user:file.expired":    (payload: { fileId: string }) => void;
  "user:file.deleted":    (payload: { fileId: string }) => void;

  "user:file.updated":    (file: PublicFile) => void;

  "user:folder.new":      (folder: PublicFolder) => void;
  "user:folder.updated":  (folder: PublicFolder) => void;
  "user:folder.deleted":  (payload: { id: string }) => void;

  "user:share.new":       (file: SharedFile) => void;
  "user:share.revoked":   (payload: { shareId: string; fileId: string }) => void;

  "user:drop.new":        (drop: import("./files.js").PublicDropLink) => void;
  "user:drop.deleted":    (payload: { id: string }) => void;
  "user:drop.used":       (drop: import("./files.js").PublicDropLink) => void;

  "user:support.ticket.updated": (ticket: PublicSupportTicket) => void;
  "user:support.message.new":    (msg: PublicSupportMessage) => void;
  "user:support.typing":         (payload: TicketTypingPayload) => void;
  "user:support.read":           (payload: TicketReadPayload) => void;
}

import type { PublicStorageServer, StorageHealth, StorageTestResult } from "./storage.js";
import type {
  MigrationProgressEvent,
  PublicMigrationItem,
  PublicMigrationJob,
} from "./migrations.js";

export interface AdminClientToServer {
  "admin:ping": (ack: Ack<{ t: number; userId: string }>) => void;
  "admin:support.join":  (payload: { ticketId: string }, ack: Ack) => void;
  "admin:support.leave": (payload: { ticketId: string }, ack: Ack) => void;
  "admin:support.typing": (payload: { ticketId: string; text: string }) => void;
  /** Abonnement à la présence live : ack = instantané complet. */
  "admin:presence.subscribe":   (ack: Ack<{ entries: PresenceEntry[] }>) => void;
  "admin:presence.unsubscribe": (ack: Ack) => void;
}
export interface AdminServerToClient {
  "admin:health.tick": (snapshot: Record<string, unknown>) => void;
  "admin:storage.updated": (server: PublicStorageServer) => void;
  "admin:storage.deleted": (payload: { id: string }) => void;
  "admin:storage.health": (payload: { id: string; health: StorageHealth }) => void;
  "admin:storage.test_result": (payload: { id: string; result: StorageTestResult }) => void;
  "admin:storage.no_candidate": (payload: {
    fileSize: number;
    tags: string[];
    strategy: string;
    rejected: { id: string; slug: string; eligible: boolean; reason: string | null }[];
  }) => void;
  "admin:upload.progress": (payload: UploadProgressInput & { ownerId: string | null }) => void;
  "admin:file.created":    (file: PublicFile) => void;
  "admin:file.available":  (file: PublicFile) => void;
  "admin:file.downloaded": (payload: { fileId: string; count: number }) => void;
  "admin:file.deleted":    (payload: { fileId: string }) => void;
  "admin:file.expired":    (payload: { fileId: string }) => void;

  "admin:file.updated":    (file: PublicFile) => void;
  "admin:migration.updated":  (job: PublicMigrationJob) => void;
  "admin:migration.progress": (payload: MigrationProgressEvent) => void;
  "admin:migration.item":     (payload: PublicMigrationItem) => void;

  "admin:av.feed.sync.started":  (payload: { feedId: string; mode: "recent" | "full" | "manual" }) => void;
  "admin:av.feed.sync.progress": (payload: { feedId: string; mode: string; added: number; total: number }) => void;
  "admin:av.feed.sync.finished": (payload: { feedId: string; mode: string; status: "ok" | "error" | "cancelled"; added: number; total: number; durationMs: number; totalHashes: number; error: string | null }) => void;

  "admin:support.ticket.new":     (ticket: PublicSupportTicket) => void;
  "admin:support.ticket.updated": (ticket: PublicSupportTicket) => void;
  "admin:support.message.new":    (msg: AdminSupportMessage) => void;
  "admin:support.message.deleted": (payload: { ticketId: string; messageId: string }) => void;
  "admin:support.typing":         (payload: TicketTypingPayload) => void;
  "admin:support.read":           (payload: TicketReadPayload) => void;

  "admin:alert": (payload: AdminAlert) => void;

  "admin:presence.upsert": (entry: PresenceEntry) => void;
  "admin:presence.remove": (payload: { id: string }) => void;
}

export type AdminAlertSeverity = "warning" | "critical";
export interface AdminAlert {
  key: string;
  severity: AdminAlertSeverity;
  titleKey: string;
  params?: Record<string, string | number>;

  href?: string;
  createdAt: string;
}

export interface PublicClientToServer {
  "public:ping": (ack: Ack<{ t: number }>) => void;
  "public:subscribe-file": (payload: { fileId: string }, ack: Ack<{ avProgress: AvProgress | null }>) => void;
  "public:unsubscribe-file": (payload: { fileId: string }) => void;
  /** Page courante de l'onglet (présence live côté admin). */
  "public:presence": (payload: PresenceUpdateInput) => void;
}
export interface PublicServerToClient {
  "public:file.event": (ev: { fileId: string; kind: "downloaded" | "expired" | "deleted" }) => void;
  "public:av.progress": (ev: AvProgress) => void;

  "public:maintenance.updated": (ev: { reason: "toggled" | "bypass_revoked" }) => void;
}

export const NS = {
  user: "/user",
  admin: "/admin",
  public: "/public",
} as const;
export type NamespaceName = (typeof NS)[keyof typeof NS];
